import { useState } from "react";
import { Mail, X, Loader2, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { User } from "@supabase/supabase-js";

interface EmailVerificationBannerProps {
  user: User | null;
}

export const EmailVerificationBanner = ({ user }: EmailVerificationBannerProps) => {
  const [isDismissed, setIsDismissed] = useState(() => {
    return sessionStorage.getItem('email_verification_banner_dismissed') === 'true';
  });
  const [isResending, setIsResending] = useState(false);

  // Check if email is confirmed
  const isVerified = !!user?.email_confirmed_at;
  const email = user?.email;

  const handleResendVerification = async () => {
    if (!email) {
      toast.error("No email address found");
      return;
    }

    setIsResending(true);
    try {
      const { error } = await supabase.auth.resend({
        type: 'signup',
        email: email,
        options: {
          emailRedirectTo: `${window.location.origin}/`,
        },
      });

      if (error) throw error;

      toast.success("Verification email sent! Check your inbox.");
    } catch (error: any) {
      toast.error(error.message || "Failed to resend verification email");
    } finally {
      setIsResending(false);
    }
  };

  const handleDismiss = () => {
    setIsDismissed(true);
    sessionStorage.setItem('email_verification_banner_dismissed', 'true');
  };

  // Don't show if no user, already verified, or dismissed
  if (!user || isVerified || isDismissed) return null;

  return (
    <div className="bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-2xl p-4 card-shadow">
      <div className="flex items-start gap-3">
        <div className="p-2 bg-amber-100 dark:bg-amber-900/50 rounded-full flex-shrink-0">
          <Mail className="w-4 h-4 text-amber-600 dark:text-amber-400" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-amber-900 dark:text-amber-100 text-sm">
            Verify your email
          </h3>
          <p className="text-xs text-amber-700 dark:text-amber-300 mt-0.5">
            Please verify {email} to receive event reminders and notifications.
          </p>
          <Button
            onClick={handleResendVerification}
            disabled={isResending}
            variant="ghost"
            size="sm"
            className="mt-2 h-8 px-3 text-amber-700 dark:text-amber-300 hover:bg-amber-100 dark:hover:bg-amber-900/50"
          >
            {isResending ? (
              <>
                <Loader2 className="w-3 h-3 mr-1.5 animate-spin" />
                Sending...
              </>
            ) : (
              <>
                <Send className="w-3 h-3 mr-1.5" />
                Resend Email
              </>
            )}
          </Button>
        </div>
        <button
          onClick={handleDismiss}
          className="text-amber-500 hover:text-amber-700 dark:hover:text-amber-300 p-2 min-w-[44px] min-h-[44px] flex items-center justify-center -mr-1"
          aria-label="Dismiss email verification banner"
        >
          <X className="w-5 h-5" />
        </button>
      </div>
    </div>
  );
};
